/**
 * Scans waiting to reach the server, persisted so a dropped connection, a
 * throttled request or a closed tab never loses one. Every scan goes through
 * here: it is queued before it is sent and removed once the server has
 * answered for good (see scanSync.ts).
 *
 * localStorage can be missing or throw (private mode, quota, blocked site
 * data), so every access is guarded and an in-memory copy keeps the queue
 * working for the life of the page.
 */

export interface QueuedScan {
  sessionId: string;
  pubId: string;
  /** ISO timestamp of when the player actually scanned */
  scannedAt: string;
  /** For messages about the scan; the session may not be loaded by then */
  pubName?: string;
}

const STORAGE_KEY = 'pendingScans';

let memory: QueuedScan[] = [];
/** False once storage has failed: memory is then the source of truth. */
let storageUsable = true;

const isQueuedScan = (value: unknown): value is QueuedScan => {
  const scan = value as QueuedScan;
  return !!scan
    && typeof scan.sessionId === 'string'
    && typeof scan.pubId === 'string'
    && typeof scan.scannedAt === 'string';
};

const read = (): QueuedScan[] => {
  if (storageUsable) {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed: unknown = raw ? JSON.parse(raw) : [];
      memory = Array.isArray(parsed) ? parsed.filter(isQueuedScan) : [];
    } catch {
      storageUsable = false;
    }
  }
  return [...memory];
};

const write = (scans: QueuedScan[]): void => {
  memory = scans;
  if (!storageUsable) return;
  try {
    if (scans.length === 0) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(scans));
    }
  } catch {
    storageUsable = false;
  }
};

const sameScan = (a: Pick<QueuedScan, 'sessionId' | 'pubId'>, b: Pick<QueuedScan, 'sessionId' | 'pubId'>) =>
  a.sessionId === b.sessionId && a.pubId === b.pubId;

export const ScanQueue = {
  /** Every queued scan, oldest first. */
  all(): QueuedScan[] {
    return read();
  },

  forSession(sessionId: string | null): QueuedScan[] {
    if (!sessionId) return [];
    return read().filter(scan => scan.sessionId === sessionId);
  },

  /** Queue a scan. A repeat scan of a pub already queued keeps the original
   *  scan time, matching the server's idempotency. */
  enqueue(scan: QueuedScan): void {
    const scans = read();
    if (scans.some(queued => sameScan(queued, scan))) return;
    write([...scans, scan]);
  },

  remove(scan: Pick<QueuedScan, 'sessionId' | 'pubId'>): void {
    write(read().filter(queued => !sameScan(queued, scan)));
  },

  clearSession(sessionId: string): void {
    write(read().filter(scan => scan.sessionId !== sessionId));
  },

  /** Test hook: forget the in-memory copy and retry storage. */
  reset(): void {
    memory = [];
    storageUsable = true;
  },
};
